import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useDivision } from "@/context/DivisionContext";
import { useAuth } from "@/context/AuthContext";
import { getDistrictByCounty, getAllDistricts } from "@/utils/txdotDistricts";
import ContractorCombobox from "@/components/ContractorCombobox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Trash2, Download, Plus } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OpenBids = () => {
  const { division } = useDivision();
  const { getAuthHeaders } = useAuth();
  const [bids, setBids] = useState([]);
  const [filteredBids, setFilteredBids] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bidToDelete, setBidToDelete] = useState(null);
  const [contractorDialogOpen, setContractorDialogOpen] = useState(false);
  const [bidToMarkWon, setBidToMarkWon] = useState(null);
  const [contractorName, setContractorName] = useState("");
  const [includeTCandC, setIncludeTCandC] = useState(false);
  const [pdfOptionsDialogOpen, setPdfOptionsDialogOpen] = useState(false);
  const [bidForPdf, setBidForPdf] = useState(null);
  const [pdfPricingOption, setPdfPricingOption] = useState("both");
  const [searchTerm, setSearchTerm] = useState("");
  const [districtFilter, setDistrictFilter] = useState("all");
  const [sortBy, setSortBy] = useState("county");
  const [sortOrder, setSortOrder] = useState("asc");
  const [addContractorDialogOpen, setAddContractorDialogOpen] = useState(false);
  const [newContractor, setNewContractor] = useState({
    name: '',
    contact_person: '',
    phone: '',
    email: ''
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchBidHistory();
  }, [division]);

  const fetchBidHistory = async () => {
    try {
      const response = await axios.get(`${API}/bids/history`, {
        params: { division }
      });
      // Filter for only pending bids (auto-deleted after 450 days if not won)
      const openBids = response.data.filter(bid => bid.status === 'pending');
      setBids(openBids);
      setFilteredBids(openBids);
      
      // Calculate analytics from current open bids only
      calculateAnalytics(openBids);
      
      setLoading(false);
    } catch (error) {
      console.error("Error fetching open bids:", error);
      toast.error("Failed to load open bids");
      setLoading(false);
    }
  };

  const calculateAnalytics = (openBids) => {
    const totalBids = openBids.length;
    const totalValue = openBids.reduce((sum, bid) => sum + (bid.final_total || 0), 0);
    
    // Count "won" bids (should be 0 for open bids, but keeping for consistency)
    const wonBids = openBids.filter(bid => bid.status === 'won').length;
    const wonValue = openBids
      .filter(bid => bid.status === 'won')
      .reduce((sum, bid) => sum + (bid.final_total || 0), 0);
    
    setAnalytics({
      total_bids: totalBids,
      total_bid_value: totalValue,
      bids_won: wonBids,
      won_value: wonValue,
      open_bids_value: totalValue  // All pending bids are "open"
    });
  };

  // Filter and sort bids
  useEffect(() => {
    let filtered = [...bids];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(bid => {
        const county = bid.project_info?.county || '';
        const highway = bid.project_info?.highway || '';
        const control = bid.project_info?.control_number || '';
        const searchLower = searchTerm.toLowerCase();
        
        return county.toLowerCase().includes(searchLower) ||
               highway.toLowerCase().includes(searchLower) ||
               control.toLowerCase().includes(searchLower);
      });
    }

    // Apply district filter (Texas only)
    if (division === 'texas' && districtFilter !== 'all') {
      filtered = filtered.filter(bid => {
        const county = bid.project_info?.county;
        const district = getDistrictByCounty(county);
        return district === districtFilter;
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aVal, bVal;
      
      switch (sortBy) {
        case 'county':
          aVal = a.project_info?.county || '';
          bVal = b.project_info?.county || '';
          break;
        case 'highway':
          aVal = a.project_info?.highway || '';
          bVal = b.project_info?.highway || '';
          break;
        case 'amount':
          aVal = a.final_total || 0;
          bVal = b.final_total || 0;
          break;
        case 'date':
          aVal = a.created_at || '';
          bVal = b.created_at || '';
          break;
        case 'days':
          aVal = a.days_since_bid || 0;
          bVal = b.days_since_bid || 0;
          break;
        default:
          aVal = a.project_info?.county || '';
          bVal = b.project_info?.county || '';
      }

      if (typeof aVal === 'string') {
        return sortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      } else {
        return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
      }
    });

    setFilteredBids(filtered);
  }, [bids, searchTerm, districtFilter, sortBy, sortOrder, division]);

  // Excel export function
  const exportToExcel = () => {
    const csvData = filteredBids.map(bid => ({
      'County': bid.project_info?.county || '',
      'Highway': bid.project_info?.highway || '',
      'Control Number': bid.project_info?.control_number || '',
      'Letting Date': bid.project_info?.letting_date || '',
      'Total LF': bid.calculation_result?.total_linear_feet || 0,
      'Milling Only Price': bid.calculation_result?.total_extended_price || 0,
      'With TC&C Price': bid.calculation_result?.total_extended_price_with_tc || 0,
      'Days Since Bid': bid.days_since_bid || 0,
      'District': division === 'texas' ? getDistrictByCounty(bid.project_info?.county) : 'N/A'
    }));

    const csv = [
      Object.keys(csvData[0]).join(','),
      ...csvData.map(row => Object.values(row).join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `open-bids-${division}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Exported to CSV successfully!');
  };

  const updateBidStatus = async (bidId, status, contractor = "", includeTC = false) => {
    try {
      await axios.patch(`${API}/bids/${bidId}/status?status=${status}&contractor_name=${encodeURIComponent(contractor)}&include_tc=${includeTC}`);
      toast.success(`Bid marked as ${status}`);
      fetchBidHistory();
      fetchAnalytics();
    } catch (error) {
      console.error("Error updating bid status:", error);
      toast.error("Failed to update bid status");
    }
  };

  const promptForContractor = (bidId) => {
    setBidToMarkWon(bidId);
    setContractorName("");
    setIncludeTCandC(false);
    setContractorDialogOpen(true);
  };

  const confirmMarkAsWon = async () => {
    if (!contractorName.trim()) {
      toast.error("Please enter a contractor name");
      return;
    }
    await updateBidStatus(bidToMarkWon, "won", contractorName, includeTCandC);
    setContractorDialogOpen(false);
    setBidToMarkWon(null);
    setContractorName("");
    setIncludeTCandC(false);
  };

  const confirmDelete = (bidId) => {
    setBidToDelete(bidId);
    setDeleteDialogOpen(true);
  };

  const openPdfOptionsDialog = (bid) => {
    setBidForPdf(bid);
    setPdfPricingOption("both");
    setPdfOptionsDialogOpen(true);
  };

  const generatePdf = () => {
    if (!bidForPdf) return;
    
    const pdfUrl = `${API}/bids/${bidForPdf.id}/pdf?option=${pdfPricingOption}`;
    window.open(pdfUrl, '_blank');
    setPdfOptionsDialogOpen(false);
    setBidForPdf(null);
  };

  const deleteBid = async () => {
    if (!bidToDelete) return;
    
    try {
      await axios.delete(`${API}/bids/${bidToDelete}`);
      toast.success("Bid deleted successfully");
      setDeleteDialogOpen(false);
      setBidToDelete(null);
      fetchBidHistory();
      fetchAnalytics();
    } catch (error) {
      console.error("Error deleting bid:", error);
      toast.error("Failed to delete bid");
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="relative min-h-screen">
      <div className="noise-overlay"></div>
      
      <div className="relative z-10 ml-64 container mx-auto px-4 py-8 max-w-7xl">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl md:text-5xl font-black tracking-tighter mb-2">
              <span className="text-[#f97316]">Open</span> Bids
            </h1>
            <p className="text-[#94a3b8] text-sm uppercase tracking-widest">
              {filteredBids.length} Pending Opportunities • ${filteredBids.reduce((sum, b) => sum + (b.final_total || 0), 0).toLocaleString()} Total Value
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={exportToExcel}
              className="millmetrix-button bg-[#10b981] hover:bg-[#059669] text-white"
            >
              Export to Excel
            </Button>
            <Button
              onClick={() => navigate('/')}
              className="millmetrix-button bg-[#f97316] hover:bg-[#ea580c] text-white"
              data-testid="back-to-calculator"
            >
              Back to Calculator
            </Button>
          </div>
        </div>

        {/* Search and Filters */}
        <Card className="millmetrix-card p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">
                Search
              </Label>
              <Input
                type="text"
                placeholder="Search by county, highway, or control number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="millmetrix-input"
              />
            </div>
            
            {division === 'texas' && (
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">
                  TXDOT District
                </Label>
                <select
                  value={districtFilter}
                  onChange={(e) => setDistrictFilter(e.target.value)}
                  className="w-full bg-[#1e293b] text-white px-4 py-2.5 rounded-lg border border-[rgba(255,255,255,0.1)] hover:border-[#f97316] focus:border-[#f97316] focus:outline-none transition-all"
                >
                  <option value="all">All Districts</option>
                  {getAllDistricts().map(district => (
                    <option key={district} value={district}>{district}</option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">
                Sort By
              </Label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full bg-[#1e293b] text-white px-4 py-2.5 rounded-lg border border-[rgba(255,255,255,0.1)] hover:border-[#f97316] focus:border-[#f97316] focus:outline-none transition-all"
              >
                <option value="county">County</option>
                <option value="highway">Highway</option>
                <option value="amount">Bid Amount</option>
                <option value="date">Date Created</option>
                <option value="days">Days Since Bid</option>
              </select>
            </div>
          </div>
          
          <div className="mt-4 flex items-center gap-2">
            <Button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              variant="outline"
              size="sm"
              className="border-[#f97316] text-[#f97316] hover:bg-[#f97316] hover:text-white"
            >
              {sortOrder === 'asc' ? '↑ Ascending' : '↓ Descending'}
            </Button>
            {(searchTerm || districtFilter !== 'all') && (
              <Button
                onClick={() => {
                  setSearchTerm('');
                  setDistrictFilter('all');
                }}
                variant="outline"
                size="sm"
                className="border-[#94a3b8] text-[#94a3b8] hover:bg-[#94a3b8] hover:text-white"
              >
                Clear Filters
              </Button>
            )}
          </div>
        </Card>

        {analytics && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <Card className="millmetrix-card p-6">
              <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">
                Current Open Bids
              </div>
              <div className="text-4xl font-mono font-bold tracking-tight tabular-nums text-[#f8fafc]">
                {analytics.total_bids || 0}
              </div>
            </Card>

            <Card className="millmetrix-card p-6">
              <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">
                Total Value
              </div>
              <div className="text-3xl font-mono font-bold tracking-tight text-[#0ea5e9] tabular-nums">
                {formatCurrency(analytics.total_bid_value || 0)}
              </div>
            </Card>

            <Card className="millmetrix-card p-6">
              <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">
                Average Bid
              </div>
              <div className="text-3xl font-mono font-bold tracking-tight text-[#f97316] tabular-nums">
                {formatCurrency(analytics.total_bids > 0 ? analytics.total_bid_value / analytics.total_bids : 0)}
              </div>
            </Card>

            <Card className="millmetrix-card p-6">
              <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">
                Letting Opportunities
              </div>
              <div className="text-4xl font-mono font-bold tracking-tight text-[#10b981] tabular-nums">
                {analytics.total_bids || 0}
              </div>
            </Card>
          </div>
        )}

        <Card className="millmetrix-card p-6">
          <h2 className="text-2xl font-bold tracking-tight mb-6 text-[#f8fafc]">Open Bids</h2>
          
          {loading ? (
            <div className="text-center py-12 text-[#94a3b8]">Loading bids...</div>
          ) : filteredBids.length === 0 ? (
            <div className="text-center py-12 text-[#94a3b8]">
              {bids.length === 0 ? 'No bids yet. Create your first bid in the calculator!' : 'No bids match your filters.'}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredBids.map((bid) => (
                <div
                  key={bid.id}
                  className="bg-[#1e293b] p-4 rounded-md border border-[rgba(255,255,255,0.05)] hover:border-[#f97316] transition-all"
                  data-testid={`bid-${bid.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-[#f8fafc]">
                          {bid.project_info?.county || 'N/A'} - {bid.project_info?.highway || 'N/A'}
                        </h3>
                        <span className={`text-sm font-mono uppercase px-2 py-1 rounded ${
                          bid.status === 'won' ? 'bg-[#10b981] text-white' : 'bg-[#0ea5e9] text-white'
                        }`}>
                          {bid.status === 'pending' ? 'OPEN' : bid.status.toUpperCase()}
                        </span>
                        {bid.status === 'pending' && bid.days_since_bid !== undefined && (
                          <span className={`text-sm font-mono px-2 py-1 rounded ${
                            bid.days_since_bid > 400 ? 'bg-[#f43f5e] text-white' : 
                            bid.days_since_bid > 300 ? 'bg-[#f97316] text-white' : 
                            'bg-[#64748b] text-white'
                          }`}>
                            {bid.days_since_bid} days since bid
                          </span>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                        <div>
                          <span className="text-[#94a3b8]">Control: </span>
                          <span className="font-mono text-[#f8fafc]">{bid.project_info?.control_number || 'N/A'}</span>
                        </div>
                        <div>
                          <span className="text-[#94a3b8]">Total LF: </span>
                          <span className="font-mono text-[#f8fafc]">
                            {bid.calculation_result?.total_linear_feet > 0 
                              ? bid.calculation_result.total_linear_feet.toLocaleString() 
                              : 'N/A'}
                          </span>
                        </div>
                        <div className="space-y-1">
                          <div>
                            <span className="text-[#94a3b8]">Milling Only: </span>
                            <span className="font-mono font-semibold text-[#10b981]">{formatCurrency(bid.calculation_result?.total_extended_price || 0)}</span>
                          </div>
                          <div>
                            <span className="text-[#94a3b8]">With TC&C: </span>
                            <span className="font-mono font-semibold text-[#0ea5e9]">{formatCurrency(bid.calculation_result?.total_extended_price_with_tc || 0)}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="text-xs text-[#94a3b8] mt-2">
                        Created: {formatDate(bid.created_at)} by <span className="text-[#f97316] font-semibold">{bid.created_by_name || 'Unknown'}</span>
                        {bid.margin_milling_only !== undefined && bid.margin_with_tc !== undefined ? (
                          <span>
                            {' | '}<span className="text-[#10b981]">M/O Margin: {bid.margin_milling_only}%</span>
                            {' | '}<span className="text-[#0ea5e9]">TC Margin: {bid.margin_with_tc}%</span>
                          </span>
                        ) : (
                          <span> | Margin: {bid.calculation_result?.overall_margin_percent || 0}%</span>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex gap-2 ml-4">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openPdfOptionsDialog(bid)}
                        className="border-[#0ea5e9] text-[#0ea5e9] hover:bg-[#0ea5e9] hover:text-white"
                        data-testid={`download-pdf-${bid.id}`}
                      >
                        PDF
                      </Button>
                      
                      {bid.status === 'pending' && (
                        <Button
                          size="sm"
                          onClick={() => promptForContractor(bid.id)}
                          className="bg-[#10b981] hover:bg-[#059669] text-white"
                          data-testid={`mark-won-${bid.id}`}
                        >
                          Mark as Won
                        </Button>
                      )}
                      
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => confirmDelete(bid.id)}
                        className="border-[#f43f5e] text-[#f43f5e] hover:bg-[#f43f5e] hover:text-white"
                        data-testid={`delete-bid-${bid.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* PDF Options Dialog */}
      <AlertDialog open={pdfOptionsDialogOpen} onOpenChange={setPdfOptionsDialogOpen}>
        <AlertDialogContent className="bg-[#0f172a] border-[#1e293b]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#f8fafc]">Generate PDF - Select Pricing Option</AlertDialogTitle>
            <AlertDialogDescription className="text-[#94a3b8]">
              Choose which pricing option(s) to include in the PDF for{' '}
              <span className="font-semibold text-[#f97316]">
                {bidForPdf?.project_info?.county} - {bidForPdf?.project_info?.highway}
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="space-y-3 my-4">
            <label className="flex items-center gap-3 p-3 rounded-md border border-[#334155] cursor-pointer hover:border-[#f97316] transition-colors">
              <input
                type="radio"
                name="pdfOption"
                value="both"
                checked={pdfPricingOption === "both"}
                onChange={(e) => setPdfPricingOption(e.target.value)}
                className="w-4 h-4"
              />
              <div>
                <div className="text-[#f8fafc] font-medium">Both Options</div>
                <div className="text-[#94a3b8] text-sm">Include both Milling Only and With TC&C pricing</div>
              </div>
            </label>

            <label className="flex items-center gap-3 p-3 rounded-md border border-[#334155] cursor-pointer hover:border-[#f97316] transition-colors">
              <input
                type="radio"
                name="pdfOption"
                value="milling_only"
                checked={pdfPricingOption === "milling_only"}
                onChange={(e) => setPdfPricingOption(e.target.value)}
                className="w-4 h-4"
              />
              <div>
                <div className="text-[#f8fafc] font-medium">Milling Only</div>
                <div className="text-[#94a3b8] text-sm">
                  Only show milling price: {formatCurrency(bidForPdf?.calculation_result?.total_extended_price || 0)}
                </div>
              </div>
            </label>

            <label className="flex items-center gap-3 p-3 rounded-md border border-[#334155] cursor-pointer hover:border-[#f97316] transition-colors">
              <input
                type="radio"
                name="pdfOption"
                value="tc_only"
                checked={pdfPricingOption === "tc_only"}
                onChange={(e) => setPdfPricingOption(e.target.value)}
                className="w-4 h-4"
              />
              <div>
                <div className="text-[#f8fafc] font-medium">With Traffic Control & Cleanup</div>
                <div className="text-[#94a3b8] text-sm">
                  Only show TC&C price: {formatCurrency(bidForPdf?.calculation_result?.total_extended_price_with_tc || 0)}
                </div>
              </div>
            </label>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel className="bg-[#1e293b] border-[#334155] text-[#f8fafc] hover:bg-[#334155]">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={generatePdf}
              className="bg-[#0ea5e9] hover:bg-[#0284c7] text-white"
            >
              Generate PDF
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-[#0f172a] border border-[rgba(255,255,255,0.1)]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#f8fafc]">Delete Bid</AlertDialogTitle>
            <AlertDialogDescription className="text-[#94a3b8]">
              Are you sure you want to delete this bid? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-[#1e293b] text-[#f8fafc] border-[rgba(255,255,255,0.1)] hover:bg-[#334155]">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={deleteBid}
              className="bg-[#f43f5e] text-white hover:bg-[#e11d48]"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Mark as Won Dialog with Contractor Combobox */}
      <Dialog open={contractorDialogOpen} onOpenChange={setContractorDialogOpen}>
        <DialogContent className="bg-[#0f172a] border border-[rgba(255,255,255,0.1)] max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#f8fafc]">Mark Bid as Won</DialogTitle>
            <DialogDescription className="text-[#94a3b8]">
              Select the contractor who won this bid and specify which amount was accepted.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2 block">
                Contractor
              </Label>
              <ContractorCombobox
                value={contractorName}
                onSelect={(name) => setContractorName(name)}
                onAddNew={() => {
                  setAddContractorDialogOpen(true);
                }}
                authHeaders={getAuthHeaders()}
                placeholder="Search or select contractor..."
              />
            </div>
            <div className="bg-[#1e293b] p-4 rounded-lg border border-[rgba(255,255,255,0.1)]">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3 block">
                Bid Amount Accepted
              </Label>
              <div className="space-y-2">
                <label className="flex items-center gap-3 p-3 rounded-md border border-[rgba(255,255,255,0.1)] hover:bg-[#334155] cursor-pointer transition-colors">
                  <input
                    type="radio"
                    name="tcOption"
                    checked={!includeTCandC}
                    onChange={() => setIncludeTCandC(false)}
                    className="w-4 h-4 text-[#f97316]"
                  />
                  <span className="text-[#f8fafc] text-sm">
                    Milling Only {bidToMarkWon && bids.find(b => b.id === bidToMarkWon) && (
                      <span className="text-[#10b981] font-mono font-semibold ml-2">
                        ${(bids.find(b => b.id === bidToMarkWon)?.calculation_result?.total_extended_price || 0).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                      </span>
                    )}
                  </span>
                </label>
                <label className="flex items-center gap-3 p-3 rounded-md border border-[rgba(255,255,255,0.1)] hover:bg-[#334155] cursor-pointer transition-colors">
                  <input
                    type="radio"
                    name="tcOption"
                    checked={includeTCandC}
                    onChange={() => setIncludeTCandC(true)}
                    className="w-4 h-4 text-[#f97316]"
                  />
                  <span className="text-[#f8fafc] text-sm">
                    With Traffic Control & Cleanup {bidToMarkWon && bids.find(b => b.id === bidToMarkWon) && (
                      <span className="text-[#10b981] font-mono font-semibold ml-2">
                        ${(bids.find(b => b.id === bidToMarkWon)?.calculation_result?.total_extended_price_with_tc || 0).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                      </span>
                    )}
                  </span>
                </label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setContractorDialogOpen(false)}
              className="bg-[#1e293b] text-[#f8fafc] border-[rgba(255,255,255,0.1)] hover:bg-[#334155]"
            >
              Cancel
            </Button>
            <Button
              onClick={confirmMarkAsWon}
              className="bg-[#10b981] hover:bg-[#059669] text-white"
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add New Contractor Quick Dialog */}
      <Dialog open={addContractorDialogOpen} onOpenChange={setAddContractorDialogOpen}>
        <DialogContent className="bg-[#0f172a] border border-[rgba(255,255,255,0.1)] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#f8fafc] flex items-center gap-2">
              <Plus className="w-5 h-5 text-[#10b981]" />
              Add New Contractor
            </DialogTitle>
            <DialogDescription className="text-[#94a3b8]">
              Quickly add a new contractor. You can add more details later on the Contractors page.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                Contractor Name *
              </Label>
              <Input
                value={newContractor.name}
                onChange={(e) => setNewContractor({ ...newContractor, name: e.target.value.toUpperCase() })}
                className="millmetrix-input uppercase mt-2"
                placeholder="e.g., ABC CONSTRUCTION LLC"
                autoFocus
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                Contact Person
              </Label>
              <Input
                value={newContractor.contact_person}
                onChange={(e) => setNewContractor({ ...newContractor, contact_person: e.target.value })}
                className="millmetrix-input mt-2"
                placeholder="e.g., John Smith"
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                Phone
              </Label>
              <Input
                value={newContractor.phone}
                onChange={(e) => setNewContractor({ ...newContractor, phone: e.target.value })}
                className="millmetrix-input mt-2"
                placeholder="e.g., (555) 123-4567"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setAddContractorDialogOpen(false);
                setNewContractor({ name: '', contact_person: '', phone: '', email: '' });
              }}
              className="bg-[#1e293b] text-[#f8fafc] border-[rgba(255,255,255,0.1)] hover:bg-[#334155]"
            >
              Cancel
            </Button>
            <Button
              onClick={async () => {
                if (!newContractor.name.trim()) {
                  toast.error('Contractor name is required');
                  return;
                }
                try {
                  await axios.post(`${API}/contractors`, newContractor, {
                    headers: getAuthHeaders(),
                    params: { division }
                  });
                  toast.success('Contractor added successfully!');
                  setContractorName(newContractor.name.toUpperCase());
                  setAddContractorDialogOpen(false);
                  setNewContractor({ name: '', contact_person: '', phone: '', email: '' });
                } catch (error) {
                  console.error('Error adding contractor:', error);
                  toast.error('Failed to add contractor');
                }
              }}
              className="bg-[#10b981] hover:bg-[#059669] text-white"
            >
              Add & Select
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OpenBids;
